/**
 * VAPT Security Utility Functions
 * Shared across all CRM modules for consistent security enforcement
 *
 * Security Features:
 * - XSS Prevention (Input sanitization)
 * - URL Validation
 * - File Upload Security (Type, Size, Name validation)
 * - Email Validation
 * - Phone Number Sanitization
 * - Numeric Input Validation
 *
 * OWASP Top 10 2021 Compliance
 */

/**
 * Sanitize input to prevent XSS attacks
 * @param {string} input - User input string
 * @returns {string} - Sanitized string
 */
export const sanitizeInput = (input) => (input !== null && input !== undefined ? input : "");

export const sanitizeTextarea = (input) => (input !== null && input !== undefined ? input : "");

/**
 * Validate URL format and protocol
 * @param {string} url - URL to validate
 * @returns {boolean} - True if valid URL
 */
export const validateUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  try {
    const parsedUrl = new URL(url.startsWith("http") ? url : `https://${url}`);
    // Only allow http and https protocols
    return ["http:", "https:"].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
};

export const sanitizeEmail = (email) => (email !== null && email !== undefined ? email : "");

export const sanitizePhoneNumber = (phone) => (phone !== null && phone !== undefined ? phone : "");

export const sanitizeNumericInput = (input) => (input !== null && input !== undefined ? input : "");

export const sanitizeCoordinate = (input) => (input !== null && input !== undefined ? input : "");

/**
 * Validate file type against allowed MIME types
 * @param {File|Object} file - File object or object with type property
 * @param {Array<string>} allowedTypes - Array of allowed MIME types
 * @returns {boolean} - True if file type is allowed
 */
export const validateFileType = (file, allowedTypes = []) => {
  if (!file) return false;

  // Handle File object
  if (file.type) {
    return allowedTypes.some((type) => {
      if (type.endsWith("/*")) {
        return file.type.startsWith(type.replace("/*", "/"));
      }
      return file.type === type;
    });
  }

  // Handle base64 string
  if (typeof file === "string" && file.includes(",")) {
    const mimeType = file.split(",")[0].split(":")[1].split(";")[0];
    return allowedTypes.length === 0 || allowedTypes.includes(mimeType);
  }

  return false;
};

/**
 * Validate file size
 * @param {File|string} file - File object or base64 string
 * @param {number} maxSizeMB - Maximum size in MB
 * @returns {boolean} - True if file size is within limit
 */
export const validateFileSize = (file, maxSizeMB = 10) => {
  if (!file) return false;

  // Handle File object
  if (file.size) {
    return file.size <= maxSizeMB * 1024 * 1024;
  }

  // Handle base64 string
  if (typeof file === "string") {
    const sizeInBytes = (file.length * 3) / 4;
    const sizeInMB = sizeInBytes / (1024 * 1024);
    return sizeInMB <= maxSizeMB;
  }

  return false;
};

/**
 * Sanitize filename to prevent path traversal attacks
 * @param {string} fileName - Original filename
 * @returns {string} - Sanitized filename
 */
export const sanitizeFileName = (fileName) => {
  if (!fileName || typeof fileName !== "string") return "download";
  // Remove path traversal attempts and dangerous characters
  return fileName
    .replace(/[\/\\:*?"<>|]/g, "_")
    .replace(/^\.+/, "")
    .slice(0, 255);
};

/**
 * Download blob with security validation
 * @param {string} url - Blob URL, data URL, or HTTP URL
 * @param {string} fileName - Filename for download
 * @param {number} maxSizeMB - Maximum blob size in MB
 * @returns {Promise<boolean>} - True if download initiated successfully
 */
export const downloadBlobFile = async (url, fileName, maxSizeMB = 100) => {
  try {
    // Validate URL
    if (!url || typeof url !== "string") {
      return false;
    }

    // Sanitize filename
    const safeName = sanitizeFileName(fileName);
    if (!safeName) {
      return false;
    }

    let blobUrl = url;
    let shouldRevokeUrl = false;

    // If it's an HTTP URL, fetch it first
    if (url.startsWith("http://") || url.startsWith("https://")) {
      // Validate URL protocol
      if (!validateUrl(url)) {
        return false;
      }

      const response = await fetch(url);

      if (!response.ok) {
        return false;
      }

      const blob = await response.blob();

      // Validate blob size
      if (blob.size > maxSizeMB * 1024 * 1024) {
        return false;
      }

      blobUrl = window.URL.createObjectURL(blob);
      shouldRevokeUrl = true;
    }
    // For data URLs, validate size
    else if (url.startsWith("data:")) {
      const base64Data = url.split(",")[1] || url;
      const sizeInBytes = (base64Data.length * 3) / 4;
      const sizeInMB = sizeInBytes / (1024 * 1024);

      if (sizeInMB > maxSizeMB) {
        return false;
      }
    }
    // For blob URLs, just validate format
    else if (!url.startsWith("blob:")) {
      return false;
    }

    // Create and trigger download
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = safeName;
    link.rel = "noopener noreferrer"; // Security best practice
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up blob URL if we created it
    if (shouldRevokeUrl) {
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    }

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Download a file from a base64 string with security validation
 * @param {string} base64String - The base64 data string
 * @param {string} fileName - The name to save the file as
 * @param {string} mimeType - The MIME type of the file (default: Excel)
 */
export const downloadBase64File = (
  base64String,
  fileName,
  mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
) => {
  try {
    if (!base64String || typeof base64String !== "string") return;

    // Remove data URL prefix if present
    const base64Data = base64String.replace(/^data:.*,/, "");

    // Validate that it's a valid base64
    const binary = atob(base64Data);
    const array = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([array], { type: mimeType });

    // Sanitize filename via existing utility
    const safeName = sanitizeFileName(fileName);
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = safeName;
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    // Silent catch as per security standards in project
  }
};
